import React, { useEffect, useState } from 'react';

import { animated, useSpring, useTrail } from '@react-spring/web';
import styled from 'styled-components';

import LessonItem from './LessonItem';
import type { LessonItemProps } from './types';

const StyledExpandableLessonList = styled.div`
  overflow: visible;
  margin-bottom: 40px;
  max-width: 1160px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const TitleBanner = styled.div`
  position: relative;
  height: 160px;
  overflow: hidden;
  border-radius: 16px;
  padding: 56px 24px;
  display: flex;
  align-items: center;

  img,
  .gatsby-image-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
  }

  @media (max-width: 768px) {
    height: 140px;
    border-radius: 8px;

    img,
    .gatsby-image-wrapper {
      border-radius: 8px;
    }
  }
`;

const TitleOverlay = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;

  h4 {
    color: white;
    margin: 0;
  }

  @media (max-width: 768px) {
    h4 {
      font-size: 24px;
    }
  }
`;

const LessonCount = styled.div`
  padding: 0 16px;
  color: var(--stile-grey-6);
  margin-top: 25px;
  margin-bottom: 11px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const ToggleButtonWrapper = styled.div`
  background-color: white;
  padding: var(--space-150, 12px) var(--space-200, 16px);
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    padding: var(--space-150, 12px) var(--space-200, 16px);
  }
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  color: var(--stile-grey-8);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0;

  &:hover {
    color: var(--stile-green-1);
  }

  svg {
    width: 16px;
    height: 16px;
    transition: transform 0.2s ease;
  }

  &.expanded svg {
    transform: rotate(180deg);
  }
`;

const LessonsList = styled.div`
  background-color: white;
  position: relative;
  overflow: hidden;
`;

const LessonsWrapper = styled.div<{ $isExpanded: boolean; $visibleCount: number }>`
  position: relative;

  ${(props) =>
    !props.$isExpanded &&
    `
    > *:nth-child(n + ${props.$visibleCount + 1}) {
      display: none;
    }
  `}
`;

const AnimatedLessonItem = styled(animated.div)`
  will-change: transform, opacity;
`;

const AnimatedGradientOverlay = styled(animated.div)`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 120px;
  background: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.8) 50%,
    rgba(255, 255, 255, 1) 100%
  );
  pointer-events: none;
  will-change: opacity;
`;

export type ExpandableLessonListProps = {
  title: React.ReactNode;
  titleBanner: React.ReactNode;
  lessons: LessonItemProps[];
  initialVisibleCount?: number;
};

export default function ExpandableLessonList({
  title,
  titleBanner,
  lessons,
  initialVisibleCount = 5,
}: ExpandableLessonListProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const toggleButtonRef = React.useRef<HTMLDivElement>(null);
  const showToggle = lessons.length > initialVisibleCount;

  // When collapsed, show lessons with links first, then others
  // When expanded, show all lessons in original order
  const displayedLessons = isExpanded
    ? lessons
    : [...lessons].sort((a, b) => {
        // Sort lessons with previewLink to the top
        if (a.previewLink && !b.previewLink) return -1;
        if (!a.previewLink && b.previewLink) return 1;
        return 0;
      });

  // Trigger animation when expansion state changes
  useEffect(() => {
    setAnimationKey((prev) => prev + 1);
  }, [isExpanded]);

  // Staggered animation for each lesson item
  const trail = useTrail(displayedLessons.length, {
    from: { opacity: 0, transform: 'translate3d(0, 20px, 0)' },
    to: { opacity: 1, transform: 'translate3d(0, 0px, 0)' },
    config: { tension: 500, friction: 30, mass: 0.3 },
    reset: animationKey > 0,
  });

  // Animate gradient overlay
  const gradientSpring = useSpring({
    opacity: isExpanded ? 0 : 1,
    config: { duration: 150 },
  });

  const handleToggle = () => {
    // If collapsing (currently expanded), scroll to toggle button
    if (isExpanded && toggleButtonRef.current) {
      // Small delay to let the collapse animation start
      setTimeout(() => {
        toggleButtonRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'end',
        });
      }, 50);
    }
    setIsExpanded(!isExpanded);
  };

  return (
    <StyledExpandableLessonList>
      <TitleBanner>
        {titleBanner}
        <TitleOverlay>
          <h4>{title}</h4>
        </TitleOverlay>
      </TitleBanner>

      <LessonCount>
        <h6 className="h7">{lessons.length} lessons</h6>
      </LessonCount>

      <LessonsList>
        <LessonsWrapper $isExpanded={isExpanded} $visibleCount={initialVisibleCount}>
          {trail.map((style, index) => (
            <AnimatedLessonItem key={`${displayedLessons[index].title}-${index}`} style={style}>
              <LessonItem {...displayedLessons[index]} />
            </AnimatedLessonItem>
          ))}
        </LessonsWrapper>

        {!isExpanded && showToggle && <AnimatedGradientOverlay style={gradientSpring} />}
      </LessonsList>

      {showToggle && (
        <ToggleButtonWrapper ref={toggleButtonRef}>
          <ToggleButton className={isExpanded ? 'expanded' : ''} onClick={handleToggle}>
            {isExpanded ? 'Hide' : 'Show'} unit outline
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M8 11L3 6L4.4 4.6L8 8.2L11.6 4.6L13 6L8 11Z" fill="currentColor" />
            </svg>
          </ToggleButton>
        </ToggleButtonWrapper>
      )}
    </StyledExpandableLessonList>
  );
}
